import type { Psbt } from 'bitcoinjs-lib';

import type { BitcoinSignPsbtRequestSignature } from '../../BitcoinWalletProvider.types';
import { InvalidPsbtError } from '../../errors/InvalidPsbtError';
import { validateSigHash } from './validateSigHash';
import { validateSigningAddress } from './validateSigningAddress';

type ValidatePsbtParams = {
  allowedSigHashTypes: number[];
  psbt: Psbt;
  signatureData?: BitcoinSignPsbtRequestSignature[];
};

/**
 * This method will validate the psbt against the signature data provided in the request
 * It checks 3 things:
 * - That the signing index exists in the input
 * - That the address provided in the request matches the address in the input,
 *   unless the disableAddressValidation flag is set
 * - That the sigHashType of the input is a member of the allowedSigHashTypes array
 */
export const validatePsbt = ({
  allowedSigHashTypes,
  psbt,
  signatureData,
}: ValidatePsbtParams) => {
  if (!signatureData?.length) {
    return;
  }

  for (const input of signatureData) {
    const {
      address: signingAddress,
      signingIndexes,
      // request from ME in cases of multi-sig. Steven wanted this defaulted to true
      disableAddressValidation = true,
    } = input;

    if (!signingIndexes?.length) {
      return;
    }

    if (!signingAddress) {
      throw new InvalidPsbtError('Missing signing address');
    }

    for (const index of signingIndexes) {
      const inputAtIndex = psbt.data.inputs[index];

      if (!inputAtIndex) {
        throw new InvalidPsbtError(`Missing input for index ${index}`);
      }

      if (!disableAddressValidation) {
        validateSigningAddress({
          index,
          inputAtIndex,
          psbt,
          signingAddress,
        });
      }

      validateSigHash({ allowedSigHashTypes, input: inputAtIndex });
    }
  }
};
